// TODO temporarily disable eslint on this file until v1.0 Unwired is complete/services are used
/* eslint @typescript-eslint/no-unused-vars: "off" */
/* eslint @typescript-eslint/no-explicit-any: "off" */
/* eslint @typescript-eslint/no-non-null-assertion: "off" */
// PublicQueryService
import { DocumentNode, gql } from "@apollo/client";

import { OrganisationType } from "../../models";
import { generateGraphQLHelpers, jsonReviver, SearchResultsGraphQL } from "../../utils";
import { apolloPublic } from "../apolloPublic";
import { BaseResponseWithMultiple, BaseResponseWithSingle, FilterGroups, ServiceError, Status } from "../Shared";
import {
  GetBadgeDetailsRequest,
  GetBadgeDetailsResponse,
  GetDeveloperDetailsRequest,
  GetDeveloperDetailsResponse,
  GetGroupDetailsRequest,
  GetInvitationDetailsRequest,
  GetInvitationDetailsResponse,
  GetProjectAggregateRequest,
  GetProjectAggregateResponse,
  GetProjectDetailsRequest,
  GetProjectDocumentsRequest,
  GetProjectDocumentsResponse,
  GetProjectIssuancesRequest,
  GetProjectIssuancesResponse,
  GetProjectTypeDetailsRequest,
  GetProjectTypeDetailsResponse,
  GetStandardDetailsRequest,
  GetStandardDetailsResponse,
  GetStandardSpecificSchemaDetailsRequest,
  GetStandardSpecificSchemaDetailsResponse,
  GetVerifierDetailsRequest,
  GetVerifierDetailsResponse,
  SearchProjectsRequest,
  SearchProjectsResponse,
  SearchProjectsResponseInvestmentDirectory,
  SearchProjectsResponseInvestmentDirectoryAspects,
  SearchProjectsResponseInvestmentDirectoryResults,
  SearchStandardsRequest,
  SearchStandardsResponse,
} from "./Types";

type SearchProjectsPreset = "investmentDirectory" | "investmentDirectoryAspects" | "investmentDirectoryResults";

type SearchProjectsFn = {
  (
    req: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>
  ): Promise<BaseResponseWithSingle<SearchProjectsResponse>>;
  (
    req: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>,
    preset: "investmentDirectory"
  ): Promise<BaseResponseWithSingle<SearchProjectsResponseInvestmentDirectory>>;
  (
    req: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>,
    preset: "investmentDirectoryAspects"
  ): Promise<BaseResponseWithSingle<SearchProjectsResponseInvestmentDirectoryAspects>>;
  (
    req: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>,
    preset: "investmentDirectoryResults"
  ): Promise<BaseResponseWithSingle<SearchProjectsResponseInvestmentDirectoryResults>>;
};

type GraphQLSelection = {
  [key: string]: true | GraphQLSelection;
};

const buildGraphQLSelection = (selection: GraphQLSelection): string =>
  Object.entries(selection)
    .map(([field, nestedSelection]) =>
      nestedSelection === true ? field : `${field} { ${buildGraphQLSelection(nestedSelection)} }`
    )
    .join("\n");

const mapPublicTagsToNames = (tags: unknown): string[] => {
  if (!Array.isArray(tags)) return [];

  return tags
    .map((tag) => {
      if (!tag || typeof tag !== "object") return null;

      if ("name" in tag && typeof tag.name === "string" && tag.name.length > 0) {
        return tag.name;
      }

      if (!("tagData" in tag)) return null;

      const rawTagData = typeof tag.tagData === "string" ? tag.tagData : null;
      if (!rawTagData) return null;

      try {
        return JSON.parse(rawTagData, jsonReviver)?.name || null;
      } catch {
        return rawTagData;
      }
    })
    .filter((tagName): tagName is string => typeof tagName === "string" && tagName.length > 0);
};

const SEARCH_PROJECTS_SELECTIONS: Record<
  "default" | SearchProjectsPreset,
  {
    aspects?: GraphQLSelection;
    results?: GraphQLSelection;
  }
> = {
  default: {
    aspects: {
      calculatedIssuancePriceMax: true,
      calculatedIssuancePriceMin: true,
      calculatedTotalQuantityMax: true,
      calculatedTotalQuantityMin: true,
      calculatedVintageEndDateMax: true,
      calculatedVintageEndDateMin: true,
      addressCountryCodes: true,
      unitMeasurements: true,
      habitatTypes: true,
      habitatConditions: true,
      regions: {
        addressCountryCode: true,
        addressRegions: true,
      },
      statuses: true,
      developers: {
        displayName: true,
        uuid: true,
      },
      standards: {
        displayName: true,
        uuid: true,
      },
      tags: {
        name: true,
      },
      badges: {
        displayName: true,
        uuid: true,
      },
      certifications: {
        displayName: true,
        uuid: true,
      },
      projectTypes: {
        displayName: true,
        uuid: true,
      },
    },
    results: {
      uuid: true,
      name: true,
      displayName: true,
      status: true,
      addressCountryCode: true,
      addressRegion: true,
      areaNetHa: true,
      tco2e: true,
      cachedUnitTotal: true,
      badges: {
        displayName: true,
        files: {
          file: {
            url: true,
          },
          type: true,
        },
        type: true,
        uuid: true,
      },
      cachedQuantityType: true,
      cachedUnitAvailableForSale: true,
      cachedPhase: true,
      developer: {
        displayName: true,
        files: {
          type: true,
          file: {
            url: true,
          },
        },
      },
      standard: {
        displayName: true,
        uuid: true,
      },
      locationCoordinates: true,
      calculatedIssuancePriceAvg: true,
      calculatedIssuancePriceMax: true,
      calculatedIssuancePriceMin: true,
      calculatedTotalQuantityMax: true,
      calculatedTotalQuantityMin: true,
      calculatedVintageEndDateMax: true,
      calculatedVintageEndDateMin: true,
      listing: {
        availableForSale: true,
        showPriceInDirectory: true,
        content: true,
        hidden: true,
        seekingFunding: true,
        pilotProject: true,
      },
      listingFiles: {
        uuid: true,
        file: {
          filename: true,
          url: true,
          uuid: true,
        },
      },
      certifications: {
        uuid: true,
        displayName: true,
        files: {
          file: {
            url: true,
          },
          type: true,
        },
        type: true,
      },
      projectType: {
        uuid: true,
        displayName: true,
      },
      landholdings: {
        groupName: true,
        groupUuid: true,
        type: true,
        calculatedTotalQuantity: true,
        calculatedTotalAvailableQuantity: true,
        calculatedTotalProjects: true,
        calculatedTotalAreaNetHa: true,
        developers: {
          id: true,
          displayName: true,
          uuid: true,
          organisationType: true,
          files: {
            uuid: true,
            type: true,
            file: {
              url: true,
              filename: true,
              mimeType: true,
              sizeBytes: true,
              uuid: true,
            },
          },
        },
        projectTypes: {
          displayName: true,
          uuid: true,
        },
      },
      unitMeasurements: true,
      tags: {
        name: true,
      },
    },
  },
  investmentDirectory: {
    aspects: {
      calculatedVintageEndDateMax: true,
      calculatedVintageEndDateMin: true,
      developers: {
        displayName: true,
        uuid: true,
      },
      projectTypes: {
        displayName: true,
        uuid: true,
      },
      regions: {
        addressCountryCode: true,
        addressRegions: true,
      },
      standards: {
        displayName: true,
        uuid: true,
      },
    },
    results: {
      addressCountryCode: true,
      addressRegion: true,
      areaNetHa: true,
      cachedQuantityType: true,
      cachedUnitAvailableForSale: true,
      cachedPhase: true,
      calculatedIssuancePriceAvg: true,
      developer: {
        displayName: true,
        files: {
          file: {
            url: true,
          },
          type: true,
        },
      },
      displayName: true,
      landholdings: {
        calculatedTotalAreaNetHa: true,
        calculatedTotalAvailableQuantity: true,
        calculatedTotalProjects: true,
        calculatedTotalQuantity: true,
        developers: {
          displayName: true,
          files: {
            file: {
              url: true,
            },
            type: true,
          },
          uuid: true,
        },
        groupName: true,
        groupUuid: true,
        projectTypes: {
          displayName: true,
          uuid: true,
        },
        type: true,
      },
      listing: {
        availableForSale: true,
        content: true,
        pilotProject: true,
        seekingFunding: true,
        showPriceInDirectory: true,
      },
      listingFiles: {
        file: {
          url: true,
        },
        uuid: true,
      },
      projectType: {
        displayName: true,
        uuid: true,
      },
      standard: {
        displayName: true,
        uuid: true,
      },
      status: true,
      tco2e: true,
      uuid: true,
    },
  },
  investmentDirectoryAspects: {
    aspects: {
      calculatedVintageEndDateMax: true,
      calculatedVintageEndDateMin: true,
      developers: {
        displayName: true,
        uuid: true,
      },
      projectTypes: {
        displayName: true,
        uuid: true,
      },
      regions: {
        addressCountryCode: true,
        addressRegions: true,
      },
      standards: {
        displayName: true,
        uuid: true,
      },
    },
  },
  investmentDirectoryResults: {
    results: {
      addressCountryCode: true,
      addressRegion: true,
      areaNetHa: true,
      cachedQuantityType: true,
      cachedUnitAvailableForSale: true,
      cachedPhase: true,
      calculatedIssuancePriceAvg: true,
      developer: {
        displayName: true,
        files: {
          file: {
            url: true,
          },
          type: true,
        },
      },
      displayName: true,
      landholdings: {
        calculatedTotalAreaNetHa: true,
        calculatedTotalAvailableQuantity: true,
        calculatedTotalProjects: true,
        calculatedTotalQuantity: true,
        developers: {
          displayName: true,
          files: {
            file: {
              url: true,
            },
            type: true,
          },
          uuid: true,
        },
        groupName: true,
        groupUuid: true,
        projectTypes: {
          displayName: true,
          uuid: true,
        },
        type: true,
      },
      listing: {
        availableForSale: true,
        content: true,
        pilotProject: true,
        seekingFunding: true,
        showPriceInDirectory: true,
      },
      listingFiles: {
        file: {
          url: true,
        },
        uuid: true,
      },
      projectType: {
        displayName: true,
        uuid: true,
      },
      standard: {
        displayName: true,
        uuid: true,
      },
      status: true,
      tco2e: true,
      uuid: true,
    },
  },
};

const searchProjectsImplementation = async (
  req: SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>,
  preset?: SearchProjectsPreset
): Promise<
  BaseResponseWithSingle<
    | SearchProjectsResponse
    | SearchProjectsResponseInvestmentDirectory
    | SearchProjectsResponseInvestmentDirectoryAspects
    | SearchProjectsResponseInvestmentDirectoryResults
  >
> => {
  const graphqlTypes = {
    "badges.displayName": "String!",
    "badges.uuid": "UUID!",
    "certifications.displayName": "String!",
    "certifications.uuid": "[UUID!]",
    "developer.displayName": "String!",
    "developer.uuid": "UUID!",
    "group.groupName": "String!",
    "group.groupUuid": "UUID!",
    "landholdings.groupName": "String!",
    "landholdings.groupUuid": "UUID!",
    "landholdings.developers.uuid": "UUID!",
    "landholdings.projectTypes.uuid": "UUID!",
    "listing.availableForSale": "Boolean!",
    "listing.pilotProject": "Boolean!",
    "listing.seekingFunding": "Boolean!",
    "listing.showPriceInDirectory": "Boolean!",
    "projectType.displayName": "String!",
    "projectType.uuid": "UUID!",
    "standard.displayName": "String!",
    "standard.uuid": "[UUID!]",
    addressCountryCode: "String!",
    addressRegion: "String!",
    areaNetHa: "Decimal!",
    cachedPhase: "String!",
    cachedQuantityType: "[String!]",
    cachedUnitAvailableForSale: "Decimal!",
    cachedUnitReserved: "Decimal!",
    cachedUnitSold: "Decimal!",
    cachedUnitTotal: "Decimal!",
    calculatedIssuancePriceMax: "Decimal!",
    calculatedIssuancePriceMin: "Decimal!",
    calculatedTotalQuantityMax: "Decimal!",
    calculatedTotalQuantityMin: "Decimal!",
    calculatedVintageEndDateMax: "Date",
    calculatedVintageEndDateMin: "Date",
    displayName: "String!",
    id: "Int!",
    name: "String!",
    status: "String!",
    tags: "String!",
    uuid: "UUID!",
    unitMeasurements: "[String!]",
  };
  const [queryBlock, variablesBlock, variables] = generateGraphQLHelpers(req, graphqlTypes);
  const selection = preset ? SEARCH_PROJECTS_SELECTIONS[preset] : SEARCH_PROJECTS_SELECTIONS.default;
  const aspectsSelection = selection.aspects ? buildGraphQLSelection(selection.aspects) : "";
  const resultsSelection = selection.results ? buildGraphQLSelection(selection.results) : "";
  const requestVariablesBlock =
    "$badgeUuid: UUID, $certificationUuid: UUID, $distinctLandholding: Boolean, $habitatCondition: String, $habitatType: String";
  const combinedVariablesBlock = [requestVariablesBlock, variablesBlock].filter(Boolean).join(", ");
  const aspectsBlock = selection.aspects
    ? `
        aspects {
          ${aspectsSelection}
        }
      `
    : "";
  const resultsBlock = selection.results
    ? `
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
        edges {
          cursor
          node {
            ${resultsSelection}
          }
        }
      `
    : "";
  const QUERY = gql(`
    query (${combinedVariablesBlock}) {
      publicSearchProjects(
        request: {
          badgeUuid: $badgeUuid
          certificationUuid: $certificationUuid
          distinctLandholding: $distinctLandholding
          habitatCondition: $habitatCondition
          habitatType: $habitatType
        }
        ${queryBlock}
      ) {
        ${aspectsBlock}
        ${resultsBlock}
      }
    }
  `);

  return apolloPublic
    .query<any>({
      query: QUERY,
      variables: {
        ...variables,
        badgeUuid: req.badgeUuid ?? null,
        certificationUuid: req.certificationUuid ?? null,
        distinctLandholding: req.distinctLandholding ?? null,
        habitatCondition: req.habitatCondition ?? null,
        habitatType: req.habitatType ?? null,
      },
    })
    .then((response) => {
      const data = response.data.publicSearchProjects as Partial<SearchResultsGraphQL<any>> & {
        aspects?: SearchProjectsResponse["aspects"] | SearchProjectsResponseInvestmentDirectory["aspects"];
        totalCount?: number;
      };
      const results = (data.edges || []).map((edge) => {
        const { node } = edge;
        if (node.listing?.content && typeof node.listing.content === "string") {
          node.listing.content = JSON.parse(node.listing.content, jsonReviver);
        }
        if (node.tags) {
          node.tags = mapPublicTagsToNames(node.tags);
        }
        return node;
      });
      const aspects =
        data.aspects && "tags" in data.aspects
          ? {
              ...data.aspects,
              tags: mapPublicTagsToNames(data.aspects.tags),
            }
          : data.aspects;

      return {
        status: Status.Success,
        data: {
          aspects,
          paging: {
            startCursor: data.pageInfo?.startCursor || "",
            endCursor: data.pageInfo?.endCursor || "",
            hasNextPage: data.pageInfo?.hasNextPage || false,
            hasPreviousPage: data.pageInfo?.hasPreviousPage || false,
            total: data.totalCount || 0,
          },
          results,
        },
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

export const searchProjects = searchProjectsImplementation as SearchProjectsFn;

interface GetProjectDetailsResponseGraphQL {
  projectDetails: any[];
}

export const getProjectDetails = async (req: GetProjectDetailsRequest): Promise<BaseResponseWithSingle<any>> => {
  const QUERY = gql`
    query ($projectUuid: UUID!) {
      projectDetails(projectUuid: $projectUuid) {
        displayName
        name
        uuid
        unitMeasurements
        addressCity
        addressCountry
        addressCountryCode
        addressRegion
        addressLine1
        dataScore
        dates
        areaNetHa
        status
        standardSpecificData
        cachedTotalQuantity
        cachedPhase
        calculatedContactable
        calculatedPiuQuantity
        calculatedVcuQuantity
        locationCoordinates
        locationGridReference
        developer {
          displayName
          uuid
          files {
            file {
              url
            }
            type
          }
          listing {
            content
          }
        }
        standard {
          displayName
          uuid
          files {
            file {
              url
            }
          }
        }
        validator {
          displayName
          uuid
          files {
            file {
              url
            }
          }
        }
        listing {
          availableForSale
          contactAlternativeEmail
          contactOptOut
          content
          hidden
          rowVersion
          showPriceInDirectory
          seekingFunding
          pilotProject
        }
        listingFiles {
          uuid
          file {
            filename
            url
            mimeType
          }
        }
        badges {
          uuid
          displayName
          files {
            file {
              url
            }
            type
          }
          type
        }
        projectType {
          displayName
          uuid
        }
        group {
          groupName
          groupUuid
        }
        projectReference {
          externalReference
        }
        standardSpecificSchemaUuid
        responsibleOwnerOrganisationUuid
      }
    }
  `;
  return apolloPublic
    .query<GetProjectDetailsResponseGraphQL>({
      query: QUERY,
      variables: { projectUuid: req.projectUuid },
    })
    .then((response) => {
      if (
        response.data.projectDetails[0].standardSpecificData &&
        typeof response.data.projectDetails[0].standardSpecificData === "string"
      )
        response.data.projectDetails[0].standardSpecificData = JSON.parse(
          response.data.projectDetails[0].standardSpecificData,
          jsonReviver
        );
      if (
        response.data.projectDetails[0].listing?.content &&
        typeof response.data.projectDetails[0].listing?.content === "string"
      )
        response.data.projectDetails[0].listing.content = JSON.parse(
          response.data.projectDetails[0].listing.content,
          jsonReviver
        );
      if (
        response.data.projectDetails[0].developer?.listing?.content &&
        typeof response.data.projectDetails[0].developer?.listing?.content === "string"
      ) {
        response.data.projectDetails[0].developer.listing.content = JSON.parse(
          response.data.projectDetails[0].developer?.listing?.content,
          jsonReviver
        );
      }

      // TODO KAN-4532 remove this after BE FIX
      if (response.data.projectDetails[0].addressRegion) {
        response.data.projectDetails[0].addressRegion = response.data.projectDetails[0].addressRegion.replaceAll(
          "�",
          "ô"
        );
      }
      return {
        status: Status.Success,
        data: response.data.projectDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetInvitationDetailsResponseGraphQL {
  invitationDetails: GetInvitationDetailsResponse[];
}

export const getInvitationDetails = async (
  req: GetInvitationDetailsRequest
): Promise<BaseResponseWithSingle<GetInvitationDetailsResponse>> => {
  const QUERY = gql`
    query ($token: UUID!) {
      invitationDetails(request: { token: $token }) {
        targetEmail
        targetFirstName
        targetLastName
        targetOrganisation {
          displayName
        }
      }
    }
  `;
  return apolloPublic
    .query<GetInvitationDetailsResponseGraphQL>({
      query: QUERY,
      variables: { token: req.token },
    })
    .then((response) => {
      return {
        status: Status.Success,
        data: response.data.invitationDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

export const getProjectIssuances = async (
  req: GetProjectIssuancesRequest
): Promise<BaseResponseWithSingle<GetProjectIssuancesResponse>> => {
  const graphqlTypes = {
    serialNumber: "String",
    habitatType: "String",
    habitat: "String",
    distinctiveness: "String",
    unitQuantityTotal: "Decimal",
    unitQuantityAvailable: "Decimal",
    price: "Decimal",
    quantityType: "String",
  };
  const [queryBlock, variablesBlock, variables] = generateGraphQLHelpers(req, graphqlTypes, false);

  const QUERY = gql`
    query ($projectUuid: UUID!, ${variablesBlock}) {
      projectIssuances(
        projectUuid: $projectUuid
        ${queryBlock}
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
        edges {
          cursor
          node {
            distinctiveness
            habitat
            habitatType
            uuid
            rowVersion
            percentageSold
            price
            issuanceDate
            unitQuantityTotal
            serialNumber
            quantityType
            standardSpecificIssuanceData
            unitMeasurement
            unitQuantityAvailable
            unitQuantityReserved
            unitQuantitySold
            unitQuantityTotal
            verifier {
              displayName
            }
            verificationStatus
            vintageStartDate
            vintageEndDate
          }
        }
      }
    }
  `;
  return apolloPublic
    .query<any>({
      query: QUERY,
      variables: { ...variables, projectUuid: req.projectUuid },
    })
    .then((response) => {
      const data = response.data.projectIssuances as SearchResultsGraphQL<
        GetProjectIssuancesResponse["results"][number]
      >;

      const results = data.edges.map((e) => e.node);
      return {
        status: Status.Success,
        data: {
          paging: {
            startCursor: data.pageInfo.startCursor,
            endCursor: data.pageInfo.endCursor,
            hasNextPage: data.pageInfo.hasNextPage,
            hasPreviousPage: data.pageInfo.hasPreviousPage,
            total: data.totalCount,
          },
          results,
        },
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

const getOrganisationDetailsHelper = (type: OrganisationType): DocumentNode => {
  let queryType = "";
  switch (type) {
    case OrganisationType.Developer:
      queryType = `query ($developerUuid: UUID!) {
        developerDetails(developerUuid: $developerUuid) {`;
      break;
    case OrganisationType.Standard:
      queryType = `query ($standardUuid: UUID!) {
          standardDetails(standardUuid: $standardUuid) {`;
      break;
    case OrganisationType.Verifier:
      queryType = `query ($verifierUuid: UUID!) {
            verifierDetails(verifierUuid: $verifierUuid) {`;
      break;
    case OrganisationType.Badge:
      queryType = `query ($badgeUuid: UUID!) {
            badgeDetails(badgeUuid: $badgeUuid) {`;
      break;
    default:
      break;
  }
  return gql`
    ${queryType}
        uuid
        websiteUrl
        rowVersion
        contactEmail
        contactPhone
        calculatedContactable
        displayName
        socialMediaFacebookUrl
        socialMediaInstagramUsername
        socialMediaLinkedInUrl
        socialMediaTwitterUsername
        socialMediaYoutubeChannel
        files {
          type
          file {
            url
          }
        }
        listing {
          content
          rowVersion
        }
        listingFiles {
          uuid
          file {
            url
          }
        }
        ${type === OrganisationType.Standard ? "projectTypes { uuid, displayName }" : ""}
        ${type === OrganisationType.Badge ? "type" : ""}
      }
    }
  `;
};

interface GetDeveloperDetailsResponseGraphQL {
  developerDetails: GetDeveloperDetailsResponse[];
}

export const getDeveloperDetails = async (
  req: GetDeveloperDetailsRequest
): Promise<BaseResponseWithSingle<GetDeveloperDetailsResponse>> => {
  const QUERY = getOrganisationDetailsHelper(OrganisationType.Developer);

  return apolloPublic
    .query<GetDeveloperDetailsResponseGraphQL>({
      query: QUERY,
      variables: { developerUuid: req.developerUuid },
    })
    .then((response) => {
      if (response.data.developerDetails[0].listing?.content)
        response.data.developerDetails[0].listing.content = JSON.parse(
          response.data.developerDetails[0].listing.content,
          jsonReviver
        );
      return {
        status: Status.Success,
        data: response.data.developerDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetStandardDetailsResponseGraphQL {
  standardDetails: GetStandardDetailsResponse[];
}

export const getStandardDetails = async (
  req: GetStandardDetailsRequest
): Promise<BaseResponseWithSingle<GetStandardDetailsResponse>> => {
  const QUERY = getOrganisationDetailsHelper(OrganisationType.Standard);

  return apolloPublic
    .query<GetStandardDetailsResponseGraphQL>({
      query: QUERY,
      variables: { standardUuid: req.standardUuid },
    })
    .then((response) => {
      if (response.data.standardDetails[0].listing?.content)
        response.data.standardDetails[0].listing.content = JSON.parse(
          response.data.standardDetails[0].listing.content,
          jsonReviver
        );
      return {
        status: Status.Success,
        data: response.data.standardDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetVerifierDetailsResponseGraphQL {
  verifierDetails: GetVerifierDetailsResponse[];
}

export const getVerifierDetails = async (
  req: GetVerifierDetailsRequest
): Promise<BaseResponseWithSingle<GetVerifierDetailsResponse>> => {
  const QUERY = getOrganisationDetailsHelper(OrganisationType.Verifier);

  return apolloPublic
    .query<GetVerifierDetailsResponseGraphQL>({
      query: QUERY,
      variables: { verifierUuid: req.verifierUuid },
    })
    .then((response) => {
      if (response.data.verifierDetails[0].listing?.content)
        response.data.verifierDetails[0].listing.content = JSON.parse(
          response.data.verifierDetails[0].listing.content,
          jsonReviver
        );
      return {
        status: Status.Success,
        data: response.data.verifierDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetBadgeDetailsResponseGraphQL {
  badgeDetails: GetBadgeDetailsResponse[];
}

export const getBadgeDetails = async (
  req: GetBadgeDetailsRequest
): Promise<BaseResponseWithSingle<GetBadgeDetailsResponse>> => {
  const QUERY = getOrganisationDetailsHelper(OrganisationType.Badge);

  return apolloPublic
    .query<GetBadgeDetailsResponseGraphQL>({
      query: QUERY,
      variables: { badgeUuid: req.badgeUuid },
    })
    .then((response) => {
      if (response.data.badgeDetails[0].listing?.content)
        response.data.badgeDetails[0].listing.content = JSON.parse(
          response.data.badgeDetails[0].listing.content,
          jsonReviver
        );
      return {
        status: Status.Success,
        data: response.data.badgeDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetProjectDocumentsResponseGraphQL {
  projectDocuments: GetProjectDocumentsResponse[];
}

export const getProjectDocuments = async (
  req: GetProjectDocumentsRequest
): Promise<BaseResponseWithMultiple<GetProjectDocumentsResponse>> => {
  const QUERY = gql`
    query ($projectUuid: UUID, $groupUuid: UUID) {
      projectDocuments(projectUuid: $projectUuid, groupUuid: $groupUuid) {
        uuid
        versionUuid
        type
        variant
        customVariant
        visibility
        createdAt
        file {
          filename
          url
          mimeType
          sizeBytes
          uuid
        }
      }
    }
  `;
  return apolloPublic
    .query<GetProjectDocumentsResponseGraphQL>({
      query: QUERY,
      variables: { projectUuid: req.projectUuid, groupUuid: req.groupUuid },
    })
    .then((response) => {
      return {
        status: Status.Success,
        data: response.data.projectDocuments,
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetProjectAggregateResponseGraphQL {
  projectAggregate: GetProjectAggregateResponse[];
}

export const getProjectAggregate = async (
  req: GetProjectAggregateRequest
): Promise<BaseResponseWithMultiple<GetProjectAggregateResponse>> => {
  const QUERY = gql`
    query (
      $filterBy: String
      $filterOperator: String
      $filterValue: String
      $groupBy: String
      $aggregate: String
      $aggregation: String
    ) {
      projectAggregate(
        request: {
          filterBy: $filterBy
          filterOperator: $filterOperator
          filterValue: $filterValue
          groupBy: $groupBy
          aggregate: $aggregate
          aggregation: $aggregation
        }
      ) {
        key
        value
      }
    }
  `;
  return apolloPublic
    .query<GetProjectAggregateResponseGraphQL>({
      query: QUERY,
      variables: {
        filterBy: req.filterBy,
        filterOperator: req.filterOperator,
        filterValue: req.filterValue,
        groupBy: req.groupBy,
        aggregate: req.aggregate,
        aggregation: req.aggregation,
      },
    })
    .then((response) => {
      return {
        status: Status.Success,
        data: response.data.projectAggregate,
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetProjectTypeDetailsResponseGraphQL {
  projectTypeDetails: GetProjectTypeDetailsResponse[];
}

export const getProjectType = async (
  req: GetProjectTypeDetailsRequest
): Promise<BaseResponseWithSingle<GetProjectTypeDetailsResponse>> => {
  const QUERY = gql`
    query ($projectTypeUuid: UUID!) {
      projectTypeDetails(projectTypeUuid: $projectTypeUuid) {
        displayName
        category
        listing {
          content
        }
        listingFiles {
          file {
            url
          }
          uuid
        }
      }
    }
  `;

  return apolloPublic
    .query<GetProjectTypeDetailsResponseGraphQL>({
      query: QUERY,
      variables: { projectTypeUuid: req.projectTypeUuid },
    })
    .then((response) => {
      if (response.data.projectTypeDetails[0].listing?.content)
        response.data.projectTypeDetails[0].listing.content = JSON.parse(
          response.data.projectTypeDetails[0].listing.content,
          jsonReviver
        );
      return {
        status: Status.Success,
        data: response.data.projectTypeDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

interface GetGroupDetailsResponseGraphQL {
  groupDetails: any[];
}

export const getPublicGroupDetails = (req: GetGroupDetailsRequest): Promise<BaseResponseWithSingle<any>> => {
  const variables = { projectGroupUuid: req.projectGroupUuid };

  const QUERY = gql`
    query ($projectGroupUuid: UUID!) {
      groupDetails(projectGroupUuid: $projectGroupUuid) {
        externalReference
        groupName
        groupGuid
        groupDescription
        manuallyUpdated
        registryMatch
        projects {
          addressCity
          addressCountryCode
          addressLine1
          addressLine2
          addressLine3
          addressRegion
          addressPostcode
          areaNetHa
          cachedTotalQuantity
          calculatedContactable
          developer {
            displayName
            listing {
              content
            }
            uuid
          }
          displayName
          duration
          listing {
            cachedHidden
            availableForSale
            content
            seekingFunding
            pilotProject
            showPriceInDirectory
          }
          listingFiles {
            file {
              url
            }
            uuid
          }
          locationCoordinates
          locationGridReference
          name
          projectType {
            displayName
            uuid
          }
          standard {
            displayName
            uuid
          }
          standardSpecificData
          startDate
          uuid
          validator {
            displayName
            uuid
          }
        }
      }
    }
  `;

  return apolloPublic
    .query<GetGroupDetailsResponseGraphQL>({
      query: QUERY,
      variables,
    })
    .then((response) => {
      if (response.data.groupDetails[0].projects && Array.isArray(response.data.groupDetails[0].projects)) {
        const parsedProjectArr = response.data.groupDetails[0].projects.map((el: any) => {
          if (el.listing?.content && typeof el.listing?.content === "string") {
            // eslint-disable-next-line no-param-reassign
            el.listing.content = JSON.parse(el.listing.content, jsonReviver);
          }

          if (el.standardSpecificData && typeof el.standardSpecificData === "string") {
            // eslint-disable-next-line no-param-reassign
            el.standardSpecificData = JSON.parse(el.standardSpecificData, jsonReviver);
          }

          return el;
        });

        response.data.groupDetails[0].projects = parsedProjectArr;
      }

      return {
        status: Status.Success,
        data: response.data.groupDetails[0],
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};
interface GetStandardSpecificSchemaDetailsResponseGraphQL {
  standardSpecificSchemaDetails: GetStandardSpecificSchemaDetailsResponse[];
}

export const getStandardSpecificSchemaDetails = async (
  req: GetStandardSpecificSchemaDetailsRequest
): Promise<BaseResponseWithSingle<GetStandardSpecificSchemaDetailsResponse>> => {
  const QUERY = gql`
    query ($standardSpecificSchemaUuid: UUID!) {
      standardSpecificSchemaDetails(standardSpecificSchemaUuid: $standardSpecificSchemaUuid) {
        definition
        schema
        standardUuid
        uuid
        validFrom
        validTo
        versionNumber
      }
    }
  `;

  return apolloPublic
    .query<GetStandardSpecificSchemaDetailsResponseGraphQL>({
      query: QUERY,
      variables: {
        standardSpecificSchemaUuid: req.standardSpecificSchemaUuid,
      },
    })
    .then((response) => {
      return {
        status: Status.Success,
        data: {
          ...response.data.standardSpecificSchemaDetails[0],
          definition: JSON.parse(response.data.standardSpecificSchemaDetails[0].definition, jsonReviver),
        },
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};

export const searchStandards = async (
  req: SearchStandardsRequest
): Promise<BaseResponseWithSingle<SearchStandardsResponse>> => {
  const graphqlTypes = {
    displayName: "String!",
    description: "String",
    "parentOrganisation.displayName": "String!",
    "projectTypes.displayName": "String!",
    "projectTypes.uuid": "UUID!",
    canCreateNewProjects: "Boolean!",
  };

  const [queryBlock, variablesBlock, variables] = generateGraphQLHelpers(req, graphqlTypes);
  const QUERY = gql`
    query (${variablesBlock}) {
      searchStandards(
        ${queryBlock}
      ) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
        edges {
          cursor
          node {
            id
            uuid
            displayName
            description
            canCreateNewProjects
            parentOrganisation {
              displayName
              uuid
            }
            projectTypes{
              uuid
              displayName
              description
              dateSchema
            }
          }
        }
      }
    }
  `;
  return apolloPublic
    .query<any>({
      query: QUERY,
      variables,
    })
    .then((response) => {
      const data = response.data.searchStandards as SearchResultsGraphQL<SearchStandardsResponse["results"][number]>;

      const results = data.edges.map((e) => e.node);
      return {
        status: Status.Success,
        data: {
          paging: {
            startCursor: data.pageInfo.startCursor,
            endCursor: data.pageInfo.endCursor,
            hasNextPage: data.pageInfo.hasNextPage,
            hasPreviousPage: data.pageInfo.hasPreviousPage,
            total: data.totalCount,
          },
          results,
        },
      };
    })
    .catch((error) => {
      const errors = error?.networkError?.result?.errors;
      const ret = errors
        ? errors.map(
            (e: any) =>
              ({
                property: e.property,
                path: e.path,
                parameters: e.extensions,
                message: e.message,
                code: e.code,
              }) as ServiceError
          )
        : [];
      return {
        status: Status.Error,
        errors: ret,
      };
    });
};
